import { machineCountLabel } from "./live-counter";
import type { CompletedCasesPresentation } from "./operator-completed-cases";

type PackagingEvidence = {
  boxesPerCase: number;
  source: "MIKRO_ERP" | "OPERATOR_CONFIRMED";
  observedAt: string;
};

export type OperatorCaseProgressInput = {
  lineName?: string;
  primaryUnit: string;
  plannedQuantity: number | null;
  producedQuantity: number | null;
  /** Ledger quantity from the same snapshot as the counter, when supplied. */
  comparisonProducedQuantity?: number | null;
  packaging: PackagingEvidence | null | undefined;
  /** The exact alternative stock-card unit whose factor is boxesPerCase. */
  packagingUnit: string | null | undefined;
  /** D100 run delta, not an independent PLC counter such as D112. */
  countedQuantity: number | null | undefined;
  countedUnit: string | null | undefined;
  counterQuality: "GOOD" | string | null | undefined;
  completedCases?: CompletedCasesPresentation;
};

export type OperatorCaseProgress = {
  actual: number | null;
  planned: number | null;
  remaining: number | null;
  overrun: number | null;
  percent: number | null;
  unit: string;
  caseEquivalent: boolean;
  completedCases: number | null;
  remainder: number | null;
  countedUnit: string | null;
  conversionLabel: string | null;
  issue: string | null;
  quantitySource?: "COMPLETED_CASES_RUN" | "COMPLETED_CASES_MACHINE";
};

function normalizedUnit(value: string | null | undefined) {
  return (value ?? "").trim().toLocaleUpperCase("tr-TR").normalize("NFKD").replace(/\p{M}/gu, "");
}

function displayUnit(value: string) {
  const normalized = normalizedUnit(value);
  if (normalized === "KOLI") return "koli";
  if (normalized === "KUTU") return "kutu";
  if (normalized === "SILINDIR") return "silindir";
  if (normalized === "ADET") return "adet";
  return value.trim().toLocaleLowerCase("tr-TR") || "birim";
}

function knownQuantity(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value >= 0 ? value : null;
}

function percentage(actual: number | null, planned: number | null) {
  return actual != null && planned != null && planned > 0 ? Number((actual / planned * 100).toFixed(12)) : null;
}

function safePackaging(input: OperatorCaseProgressInput) {
  const ratio = input.packaging?.boxesPerCase;
  const observedAt = input.packaging?.observedAt;
  const unit = normalizedUnit(input.packagingUnit);
  const allowedUnit = unit === "KUTU" || unit === "SILINDIR";
  if (!input.packaging || !["MIKRO_ERP", "OPERATOR_CONFIRMED"].includes(input.packaging.source) || typeof ratio !== "number" || !Number.isSafeInteger(ratio) || ratio <= 0 ||
      !Number.isFinite(Date.parse(observedAt ?? "")) || !allowedUnit) return null;
  return { ratio, unit: displayUnit(unit), source: input.packaging.source };
}

/**
 * Keeps the display in the persisted work-order primary unit. A D100 run delta
 * can add an exact full-case/remainder view only after it matches both the
 * current Mikro stock-card conversion and the persisted ledger. Independent PLC
 * totals are intentionally not accepted here.
 */
export function operatorCaseProgress(input: OperatorCaseProgressInput): OperatorCaseProgress {
  const primary = normalizedUnit(input.primaryUnit);
  const unit = displayUnit(primary);
  const planned = knownQuantity(input.plannedQuantity);
  const ledgerProduced = knownQuantity(input.producedQuantity);
  if (primary === "KOLI" && input.completedCases) {
    const cases = input.completedCases;
    const actual = typeof cases.value === "number" && Number.isSafeInteger(cases.value) && cases.value >= 0 ? cases.value : null;
    const runScoped = cases.source === "RUN";
    return {
      actual, planned,
      remaining: runScoped && actual !== null && planned !== null ? Math.max(0, planned - actual) : null,
      overrun: runScoped && actual !== null && planned !== null ? Math.max(0, actual - planned) : null,
      percent: runScoped ? percentage(actual, planned) : null,
      unit, caseEquivalent: true, completedCases: actual, remainder: null, countedUnit: null,
      conversionLabel: null, issue: null,
      quantitySource: runScoped ? "COMPLETED_CASES_RUN" : "COMPLETED_CASES_MACHINE",
    };
  }
  const comparisonProduced = input.comparisonProducedQuantity === undefined
    ? ledgerProduced : knownQuantity(input.comparisonProducedQuantity);
  if (primary !== "KOLI") {
    const remaining = planned != null && ledgerProduced != null ? Math.max(0, planned - ledgerProduced) : null;
    const overrun = planned != null && ledgerProduced != null ? Math.max(0, ledgerProduced - planned) : null;
    return {
      actual: ledgerProduced, planned, remaining, overrun,
      percent: percentage(ledgerProduced, planned),
      unit, caseEquivalent: false, completedCases: null, remainder: null,
      countedUnit: null, conversionLabel: null, issue: null,
    };
  }

  const packaging = safePackaging(input);
  const counterUnit = normalizedUnit(input.countedUnit);
  const counter = knownQuantity(input.countedQuantity);
  const matchingCounter = packaging !== null && counter !== null && Number.isSafeInteger(counter) &&
    input.counterQuality === "GOOD" && counterUnit === normalizedUnit(packaging.unit);
  const counterActual = matchingCounter && packaging !== null && counter !== null
    ? counter / packaging.ratio
    : null;
  // Counter entries are persisted to three decimal places. A half-thousandth
  // allows that known representation boundary, but never replaces a ledger fact.
  const counterMatchesLedger = counterActual !== null && comparisonProduced !== null &&
    Math.abs(counterActual - comparisonProduced) <= 0.0005;
  const actual = ledgerProduced;
  const remaining = planned != null && actual != null ? Math.max(0, planned - actual) : null;
  const overrun = planned != null && actual != null ? Math.max(0, actual - planned) : null;
  const issue = packaging === null
    ? "Koli içeriği Mikro stok kartından doğrulanamadı."
    : counter !== null && input.counterQuality === "GOOD" && counterUnit !== normalizedUnit(packaging.unit)
      ? "Sayaç birimi ile ürünün doğrulanmış koli dönüşümü eşleşmiyor."
      : counterActual !== null && comparisonProduced !== null && !counterMatchesLedger
        ? "Sayaç ile kaydedilen üretim henüz eşleşmiyor."
      : null;
  return {
    actual, planned, remaining, overrun,
    percent: percentage(actual, planned),
    unit, caseEquivalent: packaging !== null,
    completedCases: counterMatchesLedger && counterActual !== null ? Math.floor(counterActual) : null,
    remainder: counterMatchesLedger && matchingCounter && packaging !== null && counter !== null ? counter % packaging.ratio : null,
    countedUnit: counterMatchesLedger && packaging !== null ? packaging.source === "OPERATOR_CONFIRMED" ? "sayım birimi" : packaging.unit : null,
    conversionLabel: packaging ? `1 koli = ${packaging.ratio} ${machineCountLabel(input.lineName ?? "") === "silindir" ? "silindir" : packaging.source === "OPERATOR_CONFIRMED" ? "sayım birimi" : packaging.unit}` : null,
    issue,
  };
}
